import React, { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Button } from 'reactstrap';

import SignUpStepper from 'src/components/stepper';
import { Layout } from 'src/components/layout-wrapper/Layout';
import DiamondIcon from 'src/assets/icons/diamond.svg';
import history from 'src/lib/history';

import { useViewport, isUserFromAustralia } from 'src/lib/utils';
import { SubscriptionPageProps } from '../SubscriptionPage';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import colors from 'src/styles/equalution/colors';
import { ChallengePlanSelection } from '../components/challenge-package/ChallengePlanSelection';
import CoachUpsellCard from '../components/challenge-package/CoachUpSell';
import { setIsPersonalCoachingAdded } from 'src/store/signup';
import {
  getMonthlyPricingDisplay,
  getPersonalCoachPricing,
} from '../utils/plans.util';
import {
  trackSubscriptionUpsellViewed,
  trackSubscriptionAddonToggle,
  trackSubscriptionUpsellDeclined,
} from 'src/util/track';
import { useScrollToTop } from 'src/hooks/useScrollToTop';

export type ChallengePackageFlowProps = {
  onCheckoutClick: () => void;
  showTwoStepProcess: boolean;
};

const ChallengePackageFlow: FC<SubscriptionPageProps> = ({
  onCheckoutClick,
  showTwoStepProcess,
}) => {
  const dispatch = useAppDispatch();
  const {
    challengePackageSelectedPlan,
    isPersonalCoachingAdded,
    allPlansSorted,
  } = useAppSelector(state => state.signUp);

  const personalCoachPrice = getPersonalCoachPricing(allPlansSorted);

  const [showCoachingStep, setShowCoachingStep] = useState(false);

  const { isMobile, isDesktop } = useViewport();

  useScrollToTop(showCoachingStep);

  const widthStyles = {
    ...(isDesktop && { width: '1000px' }),
    ...(isMobile && { width: '320px' }),
  };

  const headerTitle = 'Choose your Challenge Bundle';

  const handleCheckoutClick = () => {
    if (!challengePackageSelectedPlan?.id) return;

    // Non-Australian users go directly to checkout
    if (!isUserFromAustralia()) {
      onCheckoutClick();
      return;
    }

    // Australian users: show coaching step unless already added
    if (isPersonalCoachingAdded) {
      onCheckoutClick();
    } else {
      setShowCoachingStep(true);
    }
  };

  const ExclusiveMembership = showTwoStepProcess ? null : (
    <Button
      outlined
      onClick={() => history.push('/login-account')}
      size="md"
      style={{
        width: 350,
        backgroundColor: 'transparent',
        paddingInline: '8px',
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      <Stack direction="row" alignItems="center" flexWrap="nowrap">
        <Box
          component="img"
          src={DiamondIcon}
          alt="diamond"
          sx={{ color: colors.SALAD, flexShrink: 0 }}
        />
        <Box
          ml={'12px'}
          color={colors.SALAD}
          fontSize={'16px'}
          fontWeight={600}
          sx={{ whiteSpace: 'nowrap' }}>
          Log in for exclusive Member Pricing
        </Box>
      </Stack>
    </Button>
  );

  const HeaderAndStepper = () => (
    <Stack mb={isMobile ? '24px' : '54px'} alignItems={'center'}>
      <Box width={showTwoStepProcess ? 70 : 100}>
        <SignUpStepper
          activeStep={1}
          steps={showTwoStepProcess ? ['1', '2'] : ['1', '2', '3']}
        />
      </Box>
      <Box
        position="relative"
        width="100%"
        mb={isMobile ? '24px' : '24px'}
        mt="20px"
        sx={widthStyles}>
        <Box
          sx={{
            fontSize: isMobile ? '20px' : '29px',
            fontWeight: 800,
            textAlign: 'center',
          }}>
          {headerTitle}
        </Box>
      </Box>
    </Stack>
  );

  React.useEffect(() => {
    if (!showCoachingStep) {
      return;
    }

    trackSubscriptionUpsellViewed(
      'nutrition_coaching',
      challengePackageSelectedPlan?.checkoutPlanId || undefined
    );
  }, [challengePackageSelectedPlan?.checkoutPlanId, showCoachingStep]);

  if (showCoachingStep) {
    return (
      <Layout module="login">
        <CoachUpsellCard
          onAddCoach={() => {
            trackSubscriptionAddonToggle(
              true,
              'nutrition_coaching',
              challengePackageSelectedPlan?.checkoutPlanId || undefined
            );
            dispatch(setIsPersonalCoachingAdded(true));
            onCheckoutClick();
          }}
          onSkip={() => {
            trackSubscriptionUpsellDeclined(
              'nutrition_coaching',
              challengePackageSelectedPlan?.checkoutPlanId || undefined
            );
            onCheckoutClick();
          }}
          price={personalCoachPrice}
          onBack={() => setShowCoachingStep(false)}
        />
      </Layout>
    );
  }

  return (
    <Layout module="login">
      <Stack
        alignItems={'center'}
        sx={{
          padding: '1.5rem',
          marginX: 'auto',
          ...widthStyles,
        }}>
        <HeaderAndStepper />

        <ChallengePlanSelection />

        <Box
          textAlign={'center'}
          width={'100%'}
          fontSize={'15px'}
          fontWeight={600}
          marginY={'30px'}>
          After the challenge, your Standard Plan subscription automatically
          renews at{' '}
          <span
            style={{
              color: colors.SALAD,
            }}>
            {getMonthlyPricingDisplay()}/month
          </span>{' '}
          unless cancelled. No discounts can be used for challenge entry.
        </Box>
        <Button
          style={{ width: 350 }}
          size="md"
          disabled={!challengePackageSelectedPlan?.id}
          onClick={handleCheckoutClick}>
          Continue to Checkout
        </Button>
        <Box height={'20px'} />

        {ExclusiveMembership}

        <Box height={'100px'} />
      </Stack>
    </Layout>
  );
};

export default ChallengePackageFlow;
